/**
 * Driver WebHID per tavolette firma Wacom STU (STU-430 e modelli compatibili).
 * Basato sul protocollo STU via feature report (senza SDK proprietario Wacom).
 * Richiede Chrome/Edge/Chromium e connessione HTTPS o localhost.
 */

export const WACOM_VENDOR_ID = 0x056a;

/** Product ID → dimensioni schermo e range coordinate penna */
export const STU_DEVICE_CONFIG = {
  0x00a1: { name: "STU-500", width: 640, height: 400, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a2: { name: "STU-300", width: 396, height: 100, rawMaxX: 9600, rawMaxY: 2400 },
  0x00a3: { name: "STU-520", width: 800, height: 480, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a4: { name: "STU-430", width: 320, height: 200, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a5: { name: "STU-530", width: 640, height: 400, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a6: { name: "STU-430V", width: 320, height: 200, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a8: { name: "STU-540", width: 800, height: 480, rawMaxX: 9600, rawMaxY: 6000 },
  0x00a9: { name: "STU-541", width: 800, height: 480, rawMaxX: 9600, rawMaxY: 6000 },
};

export const STU_PRODUCT_IDS = Object.keys(STU_DEVICE_CONFIG).map((id) => Number(id));

export function isWebHidSupported() {
  return typeof navigator !== "undefined" && "hid" in navigator;
}

const COMMAND = {
  penData: 0x01,
  capability: 0x09,
  writingMode: 0x0e,
  clearScreen: 0x20,
  inkMode: 0x21,
  penColorAndWidth: 0x2d,
  writingArea: 0x2a,
  penDataTiming: 0x34,
};

/** Decodifica un report penna STU (formato Wacom GSS / WebHID di riferimento). */
export function parseStuPenData(dataView, pressureMax = 1023) {
  const status = dataView.getUint8(0);
  const rdy = (status & 1) !== 0;
  const sw = (status & 2) !== 0;
  const x = dataView.getUint16(2);
  const y = dataView.getUint16(4);
  // I bit rdy/sw condividono il primo byte con la pressione: mascherare il nibble alto.
  const pressRaw = ((status & 0x0f) << 8) | dataView.getUint8(1);
  const maxPressure = pressureMax > 0 ? pressureMax : 1023;
  return {
    rdy,
    sw,
    x,
    y,
    pressRaw,
    press: pressRaw / maxPressure,
  };
}

export class WacomStuDriver {
  constructor() {
    this.device = null;
    this.config = null;
    this._onPenData = null;
    this._inputHandler = null;
  }

  get isConnected() {
    return Boolean(this.device?.opened);
  }

  get deviceName() {
    return this.config?.name || "Wacom STU";
  }

  async connect() {
    if (this.isConnected) return true;

    const filters = STU_PRODUCT_IDS.map((productId) => ({
      vendorId: WACOM_VENDOR_ID,
      productId,
    }));

    let devices = (await navigator.hid.getDevices()).filter(
      (d) => d.vendorId === WACOM_VENDOR_ID && STU_PRODUCT_IDS.includes(d.productId)
    );

    if (devices.length === 0) {
      devices = await navigator.hid.requestDevice({ filters });
    }

    if (!devices?.length) return false;

    this.device = devices[0];
    this.config = STU_DEVICE_CONFIG[this.device.productId] || STU_DEVICE_CONFIG[0x00a4];

    if (!this.device.opened) {
      await this.device.open();
    }

    this._bindInputReports();
    await this._loadDeviceCapabilities();
    await this._initializeTablet();
    return true;
  }

  async _loadDeviceCapabilities() {
    try {
      const data = await this.device.receiveFeatureReport(COMMAND.capability);
      const tabletMaxX = data.getUint16(1);
      const tabletMaxY = data.getUint16(3);
      const pressureMax = data.getUint16(5) || 1023;
      const screenWidth = data.getUint16(7) || this.config.width;
      const screenHeight = data.getUint16(9) || this.config.height;
      this.config = {
        ...this.config,
        tabletMaxX,
        tabletMaxY,
        pressureMax,
        screenWidth,
        screenHeight,
        rawMaxX: tabletMaxX,
        rawMaxY: tabletMaxY,
      };
    } catch (err) {
      console.warn("Impossibile leggere capabilities tavoletta, uso valori predefiniti:", err);
    }
  }

  async disconnect() {
    if (this._inputHandler && this.device) {
      this.device.removeEventListener("inputreport", this._inputHandler);
      this._inputHandler = null;
    }
    if (this.device?.opened) {
      try {
        await this.setInking(false);
        await this.device.close();
      } catch {
        /* ignore */
      }
    }
    this.device = null;
    this.config = null;
  }

  onPenData(callback) {
    this._onPenData = callback;
  }

  async clearScreen() {
    await this._sendFeature(COMMAND.clearScreen, new Uint8Array([0]));
  }

  async setInking(enabled) {
    await this._sendFeature(COMMAND.inkMode, new Uint8Array([enabled ? 1 : 0]));
  }

  async setPenColorAndWidth(width = 2) {
    await this._sendFeature(COMMAND.penColorAndWidth, new Uint8Array([0, 0, 0, width]));
  }

  async _initializeTablet() {
    const { width, height } = this.config;
    await this.clearScreen();
    await this._sendFeature(COMMAND.writingMode, new Uint8Array([1]));
    const area = new Uint8Array(8);
    const view = new DataView(area.buffer);
    view.setUint16(0, 0, true);
    view.setUint16(2, 0, true);
    view.setUint16(4, width, true);
    view.setUint16(6, height, true);
    await this._sendFeature(COMMAND.writingArea, area);
    await this.setPenColorAndWidth(2);
    await this.setInking(true);
  }

  _bindInputReports() {
    if (this._inputHandler) {
      this.device.removeEventListener("inputreport", this._inputHandler);
    }
    this._inputHandler = (event) => {
      if (!this._onPenData) return;
      if (event.reportId !== COMMAND.penData && event.reportId !== COMMAND.penDataTiming) {
        return;
      }
      const packet = parseStuPenData(event.data, this.config?.pressureMax);
      this._onPenData(packet);
    };
    this.device.addEventListener("inputreport", this._inputHandler);
  }

  async _sendFeature(reportId, data) {
    if (!this.isConnected) return;
    await this.device.sendFeatureReport(reportId, data);
  }
}
